import React, { useContext, useEffect, useState } from "react";
import { Box, Typography, Grid, Divider } from "@mui/material";
import NoteCard from "./NoteCard";
import Ask7G from "../../assets/interaction-detail-page/7Gpowered.svg";
import UserInteractionsService from "../../services/interactions/Interactions";
import ProgressSpinner from "../Common/ProgressSpinner";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import "../../styles/common.css";
import SlideOver from "../Common/SlideOver";
import NotesSlideOver from "./NotesSlideOver";
import { GlobalContext } from "../../context/GlobalContext";

interface NotePageProps {
  id: string;
  isInteractionAttendee?: boolean;
}
interface Note {
  interactionId: string;
  sentimentScore: string;
  overAllSentiment: string;
  interactionStartTime: string;
  sentiment: {
    analysisTime?: string | null;
    overallSentiment?: string | null;
    sentimentSummary?: string | null;
    sentimentScore?: string | null;
  } | null;
  summary: string;
}

const NotePage: React.FC<NotePageProps> = ({id}) => {
    const [notes, setNotes] = useState<Note[]>([]);
    const { showSnackbar } = useDisplaySnackbar();
    const [showSpinner, setShowSpinner] = useState(false);
    const [isSlideOverOpen, setIsSlideOverOpen] = useState(false);
    const [selectedNote, setSelectedNote] = useState<Note | null>(null);
    const { impersonationDetails } = useContext(GlobalContext);
    const impersonatingUserPorId = impersonationDetails?.impersonatingUserPorId;
    const accessLevel = impersonationDetails?.accessType;

    const handleCardClick = (note: Note) => {
    setSelectedNote(note);
    setIsSlideOverOpen(true);
    };

    const 
    {
        isLoading: isNotesLoading,
        isFetching: isNotesFetching,
        isSuccess: isNotesSuccess, 
        isError: isNotesError,
        error: notesError, 
        data: notesData 
    } = UserInteractionsService.useGetInteractionNotes(
        {
            interactionId: id || "",
            impersonatingPorId: impersonatingUserPorId,
            accessLevel: accessLevel,
        }, true
    );

    useEffect(() => {
    setShowSpinner(isNotesLoading || isNotesFetching);
    if(isNotesSuccess && notesData) {
        setNotes(notesData?.interactions || []);
    }
    else if (isNotesError){
        showSnackbar("error", notesError || "Failed to fetch notes");
    }
    }, [isNotesLoading, isNotesFetching, isNotesSuccess, isNotesError, notesError, notesData]);

    return (
    <>
    <Box className="p2p-page-container">
        <Box className="p2p-header-section" >
        <Typography variant="body2" className="p2p-text-title">
            Past to Present
        </Typography>
        <Typography className="p2p-text-powered">
            <Box display="flex" alignItems="center" gap={1}>
                <img src={Ask7G} width={20} alt="7G" />
                Powered by 7G
            </Box>
        </Typography>
        </Box>
        <Divider sx={{ my: 2, color: "#E5E5E5" }}/>

        {notes.length === 0 ? (
        <Box textAlign="center" width="100%" py={4} color="#43434D">
            No Notes Available
        </Box>
        ) : (
        <Grid container rowSpacing={2} columnSpacing={2} paddingLeft={3} paddingRight={3}>
            {notes.map((note, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
                <NoteCard
                date={note.interactionStartTime}
                sentiment={note.overAllSentiment}
                summary={note.sentiment?.sentimentSummary || "No summary available"}
                onClick={() => handleCardClick(note)}
                />
            </Grid>
            ))}
        </Grid>
        )}
    </Box>
    <ProgressSpinner showSpinner={showSpinner}/>
    <SlideOver isOpen={isSlideOverOpen} onClose={() => setIsSlideOverOpen(false)}>
        {selectedNote && (
            <NotesSlideOver
                isSlideOverOpen={isSlideOverOpen}
                note={selectedNote}
            />
        )}
    </SlideOver>
    </>
    );
};

export default NotePage;
